// /////////////////////////////////////////////////////////////
// [ファイル] SymbolTree.tsx
// [概要] ソース品質管理プラットフォームの左ペイン。
//        プロジェクト > ファイル > シンボル の 3 階層ツリー。
//        各シンボルにレビュー状態のドットと未対応課題数バッジを表示し、
//        クリックで選択する。ファイル単位で折り畳み可能。
// /////////////////////////////////////////////////////////////
import React, { useState } from 'react';
import { Box, Typography, Collapse, Chip } from '@mui/material';
import * as Icons from '../../constants/icons';
import {
  SourceModule,
  SYMBOL_KIND_LABEL,
} from '../../types/sourceQuality';
import {
  REVIEW_STATUS_COLOR,
  PROJECT_LABEL,
  PROJECT_ORDER,
} from './statusVisuals';

interface SymbolTreeProps {
  modules: SourceModule[];
  selectedSymbolId: string | null;
  onSelect: (symbolId: string) => void;
}

const StatusDot: React.FC<{ color: string }> = ({ color }) => (
  <Box
    sx={{
      width: 9,
      height: 9,
      borderRadius: '50%',
      bgcolor: color,
      flexShrink: 0,
    }}
  />
);

export default function SymbolTree({
  modules,
  selectedSymbolId,
  onSelect,
}: SymbolTreeProps) {
  // 既定でファイルを全展開。閉じたファイルの module_id を保持する。
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

  const toggle = (moduleId: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(moduleId)) next.delete(moduleId);
      else next.add(moduleId);
      return next;
    });
  };

  return (
    <Box sx={{ py: 1 }}>
      {PROJECT_ORDER.map((project) => {
        const projectModules = modules.filter((m) => m.project === project);
        if (projectModules.length === 0) return null;
        return (
          <Box key={project} sx={{ mb: 1 }}>
            {/* プロジェクト見出し */}
            <Typography
              variant="caption"
              sx={{
                px: 1.5,
                py: 0.5,
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                color: 'text.secondary',
                fontWeight: 700,
                letterSpacing: '0.04em',
              }}
            >
              <Icons.Code sx={{ fontSize: 15 }} />
              {PROJECT_LABEL[project]}
            </Typography>

            {projectModules.map((m) => {
              const isOpen = !collapsed.has(m.module_id);
              const fileName = m.file_path.split('/').pop() ?? m.file_path;
              return (
                <Box key={m.module_id}>
                  {/* ファイル行 (折り畳みトグル) */}
                  <Box
                    onClick={() => toggle(m.module_id)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      px: 1.5,
                      py: 0.5,
                      cursor: 'pointer',
                      '&:hover': { bgcolor: 'action.hover' },
                    }}
                  >
                    {isOpen ? (
                      <Icons.FolderOpen sx={{ fontSize: 16, color: '#90a4ae' }} />
                    ) : (
                      <Icons.Folder sx={{ fontSize: 16, color: '#90a4ae' }} />
                    )}
                    <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 500 }}>
                      {fileName}
                    </Typography>
                  </Box>

                  {/* シンボル行 */}
                  <Collapse in={isOpen} timeout="auto" unmountOnExit>
                    {m.symbols.map((s) => {
                      const openIssues = s.issues.filter((i) => i.status === 'open').length;
                      const selected = s.symbol_id === selectedSymbolId;
                      return (
                        <Box
                          key={s.symbol_id}
                          onClick={() => onSelect(s.symbol_id)}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            pl: 4,
                            pr: 1.5,
                            py: 0.5,
                            cursor: 'pointer',
                            borderLeft: '3px solid',
                            borderLeftColor: selected ? 'primary.main' : 'transparent',
                            bgcolor: selected ? 'action.selected' : 'transparent',
                            '&:hover': { bgcolor: selected ? 'action.selected' : 'action.hover' },
                          }}
                        >
                          <StatusDot color={REVIEW_STATUS_COLOR[s.review_status]} />
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: '0.8rem',
                              fontFamily: 'monospace',
                              flexGrow: 1,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {s.name}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ color: 'text.disabled', fontSize: '0.65rem', flexShrink: 0 }}
                          >
                            {SYMBOL_KIND_LABEL[s.kind]}
                          </Typography>
                          {openIssues > 0 && (
                            <Chip
                              label={openIssues}
                              size="small"
                              sx={{
                                height: 16,
                                minWidth: 16,
                                fontSize: '0.6rem',
                                bgcolor: '#e53935',
                                color: '#fff',
                                '& .MuiChip-label': { px: 0.5 },
                              }}
                            />
                          )}
                        </Box>
                      );
                    })}
                  </Collapse>
                </Box>
              );
            })}
          </Box>
        );
      })}
    </Box>
  );
}
